import React, { useState, useEffect } from 'react';
import ApiClient, { Form } from '../api/client';
import { ModalShell } from './ui';

interface FormDialogProps {
  apiClient: ApiClient;
  form?: Form;
  onClose: () => void;
  onSubmit: (data: {
    name: string;
    domain: string;
    allowedOrigins: string[];
    description?: string;
    testMode?: boolean;
    spamThreshold?: number | null;
    notificationsEnabled?: boolean;
    spamDetectionDisabled?: boolean;
    adapterType?: 'flat' | 'event_envelope';
    eventTypePath?: string | null;
    unwrapPath?: string | null;
  }) => void;
}

const FormDialog: React.FC<FormDialogProps> = ({ apiClient, form, onClose, onSubmit }) => {
  const [name, setName] = useState('');
  const [domain, setDomain] = useState('');
  const [origins, setOrigins] = useState('');
  const [description, setDescription] = useState('');
  const [testMode, setTestMode] = useState(false);
  const [spamThreshold, setSpamThreshold] = useState<string>('');
  const [notificationsEnabled, setNotificationsEnabled] = useState(true);
  const [spamDetectionDisabled, setSpamDetectionDisabled] = useState(false);

  // Inbound source adapter config
  const [adapterType, setAdapterType] = useState<'flat' | 'event_envelope'>('flat');
  const [eventTypePath, setEventTypePath] = useState('');
  const [unwrapPath, setUnwrapPath] = useState('');
  const [hasSigningSecret, setHasSigningSecret] = useState(false);
  const [revealedSecret, setRevealedSecret] = useState<string | null>(null);
  const [rotating, setRotating] = useState(false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (form) {
      setName(form.name);
      setDomain(form.domain);
      setOrigins(form.allowedOrigins?.join('\n') || '');
      setDescription(form.description || '');
      setTestMode(form.testMode || false);
      setSpamThreshold(form.spamThreshold != null ? String(form.spamThreshold) : '');
      setNotificationsEnabled(form.notificationsEnabled !== false);
      setSpamDetectionDisabled(form.spamDetectionDisabled === true);
      setAdapterType(form.adapterType ?? 'flat');
      setEventTypePath(form.eventTypePath ?? '');
      setUnwrapPath(form.unwrapPath ?? '');
      setHasSigningSecret(form.hasSigningSecret === true);
    }
  }, [form]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const handleRotateSecret = async () => {
    if (!form?.id) return;
    if (hasSigningSecret) {
      const confirmed = window.confirm(
        'Rotating the secret will immediately invalidate the current one. ' +
        'Any source system signing with the old secret will start failing until updated. Continue?'
      );
      if (!confirmed) return;
    }
    setRotating(true);
    setError(null);
    try {
      const result = await apiClient.rotateFormSigningSecret(form.id);
      setRevealedSecret(result.signingSecret);
      setHasSigningSecret(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to rotate signing secret');
    } finally {
      setRotating(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!name.trim() || !domain.trim() || !origins.trim()) {
      setError('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      const allowedOrigins = origins
        .split('\n')
        .map((o) => o.trim())
        .filter((o) => o);

      const thresholdValue = spamThreshold.trim()
        ? parseFloat(spamThreshold)
        : null;

      await onSubmit({
        name,
        domain,
        allowedOrigins,
        description: description || undefined,
        testMode,
        spamThreshold: thresholdValue,
        notificationsEnabled,
        spamDetectionDisabled,
        adapterType,
        eventTypePath: adapterType === 'event_envelope' ? (eventTypePath || null) : null,
        unwrapPath: adapterType === 'event_envelope' ? (unwrapPath || null) : null,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  return (
    <ModalShell
      title={form ? 'Edit Form' : 'Create Form'}
      onClose={onClose}
      className="form-dialog"
      footer={
        <>
          <button className="btn btn-secondary" onClick={onClose} disabled={loading}>
            Cancel
          </button>
          <button
            className="btn btn-primary"
            type="submit"
            form="form-dialog-form"
            disabled={loading}
          >
            {loading ? 'Saving...' : form ? 'Update Form' : 'Create Form'}
          </button>
        </>
      }
    >
        <form id="form-dialog-form" onSubmit={handleSubmit}>
          {error && (
            <div className="alert alert-error">
              <span>Warning</span>
              <span>{error}</span>
            </div>
          )}

          <div className="form-group">
            <label htmlFor="name">Form Name *</label>
            <input
              id="name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Contact Form"
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="domain">Domain *</label>
            <input
              id="domain"
              type="text"
              value={domain}
              onChange={(e) => setDomain(e.target.value)}
              placeholder="e.g., example.com"
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="origins">
              Allowed Origins * <small>(one per line)</small>
            </label>
            <textarea
              id="origins"
              value={origins}
              onChange={(e) => setOrigins(e.target.value)}
              placeholder="https://example.com&#10;https://www.example.com"
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="description">Description (optional)</label>
            <textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe this form..."
              disabled={loading}
              style={{ minHeight: '60px' }}
            />
          </div>

          {/* Notifications Settings */}
          <div style={{ borderTop: '1px solid #e0e0e0', paddingTop: '16px', marginTop: '8px' }}>
            <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', color: '#666' }}>Notification Settings</h4>

            <div className="form-group">
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={notificationsEnabled}
                  onChange={(e) => setNotificationsEnabled(e.target.checked)}
                  disabled={loading}
                  style={{ width: 'auto' }}
                />
                <span>Enable notifications</span>
              </label>
              <p style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                When disabled, no notifications are sent for this form (Slack, email, Pushover, ConvertKit).
              </p>
            </div>
          </div>

          {/* Spam Detection Settings */}
          <div style={{ borderTop: '1px solid #e0e0e0', paddingTop: '16px', marginTop: '8px' }}>
            <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', color: '#666' }}>Spam Detection Settings</h4>

            <div className="form-group">
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={spamDetectionDisabled}
                  onChange={(e) => setSpamDetectionDisabled(e.target.checked)}
                  disabled={loading}
                  style={{ width: 'auto' }}
                />
                <span>Disable Spam Detection</span>
                {spamDetectionDisabled && (
                  <span style={{
                    background: '#f8d7da',
                    color: '#721c24',
                    padding: '2px 8px',
                    borderRadius: '4px',
                    fontSize: '11px',
                    fontWeight: 500,
                  }}>
                    DISABLED
                  </span>
                )}
              </label>
              <p style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                Skips all spam checks (IP blacklist, ASN reputation, heuristics, AI). All submissions are auto-approved. Use for PBX/IVR forms or trusted webhook sources.
              </p>
            </div>

            {!spamDetectionDisabled && (<>
            <div className="form-group">
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={testMode}
                  onChange={(e) => setTestMode(e.target.checked)}
                  disabled={loading}
                  style={{ width: 'auto' }}
                />
                <span>Test Mode</span>
                {testMode && (
                  <span style={{
                    background: '#fff3cd',
                    color: '#856404',
                    padding: '2px 8px',
                    borderRadius: '4px',
                    fontSize: '11px',
                    fontWeight: 500,
                  }}>
                    ENABLED
                  </span>
                )}
              </label>
              <p style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                Submissions show real spam status but IPs are not blacklisted. Notifications are marked [TEST].
              </p>
            </div>

            <div className="form-group">
              <label htmlFor="spamThreshold">
                Spam Threshold <small>(0.0 - 1.0, leave empty for default: 0.8)</small>
              </label>
              <input
                id="spamThreshold"
                type="number"
                min="0"
                max="1"
                step="0.05"
                value={spamThreshold}
                onChange={(e) => setSpamThreshold(e.target.value)}
                placeholder="0.8"
                disabled={loading}
                style={{ maxWidth: '150px' }}
              />
              <p style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                Submissions with spam scores above this threshold will be marked as spam.
              </p>
            </div>
            </>)}
          </div>

          {/* Inbound Source Settings */}
          <div style={{ borderTop: '1px solid #e0e0e0', paddingTop: '16px', marginTop: '8px' }}>
            <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', color: '#666' }}>Inbound Source</h4>

            <div className="form-group">
              <label htmlFor="adapterType">Adapter Type</label>
              <select
                id="adapterType"
                value={adapterType}
                onChange={(e) => setAdapterType(e.target.value as 'flat' | 'event_envelope')}
                disabled={loading}
                style={{ maxWidth: '300px' }}
              >
                <option value="flat">Flat (default — body becomes the submission)</option>
                <option value="event_envelope">Event Envelope (Stripe / LawnWire / generic webhook)</option>
              </select>
              <p style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                {adapterType === 'flat' ? (
                  <>The default for HTML forms and PBX-style flat JSON. The whole body is treated as the submission.</>
                ) : (
                  <>For webhooks that wrap the payload in an envelope (e.g. <code>{'{ "type": "x", "data": {...} }'}</code>). Configure where to find the event type and the inner payload below.</>
                )}
              </p>
            </div>

            {adapterType === 'event_envelope' && (
              <>
                <div className="form-group">
                  <label htmlFor="eventTypePath">Event Type Path</label>
                  <input
                    id="eventTypePath"
                    type="text"
                    value={eventTypePath}
                    onChange={(e) => setEventTypePath(e.target.value)}
                    placeholder="e.g., type"
                    disabled={loading}
                    style={{ maxWidth: '400px', fontFamily: 'monospace', fontSize: '13px' }}
                  />
                  <p style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                    Dot-notation path to the event type within the payload. Stored on the submission's <code>eventType</code> column for routing/filtering. Leave blank to skip.
                  </p>
                </div>

                <div className="form-group">
                  <label htmlFor="unwrapPath">Unwrap Path</label>
                  <input
                    id="unwrapPath"
                    type="text"
                    value={unwrapPath}
                    onChange={(e) => setUnwrapPath(e.target.value)}
                    placeholder="e.g., data"
                    disabled={loading}
                    style={{ maxWidth: '400px', fontFamily: 'monospace', fontSize: '13px' }}
                  />
                  <p style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                    Dot-notation path to the inner payload that should become the submission data. Leave blank to keep the full envelope as the submission.
                  </p>
                </div>
              </>
            )}

            {/* Signing secret — only available on existing forms */}
            {form?.id && (
              <div className="form-group" style={{ padding: '12px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '6px' }}>
                <label style={{ marginBottom: '4px' }}>
                  HMAC Signing Secret
                  {hasSigningSecret && !revealedSecret && (
                    <span style={{
                      background: '#d4edda',
                      color: '#155724',
                      padding: '2px 8px',
                      borderRadius: '4px',
                      fontSize: '11px',
                      marginLeft: '8px',
                    }}>
                      CONFIGURED
                    </span>
                  )}
                </label>
                <p style={{ fontSize: '12px', color: '#666', margin: '4px 0 8px' }}>
                  When set, this form's adapter will verify the <code>X-FP-Signature</code> header on incoming requests using HMAC-SHA256 of the raw body with this secret. Requests with missing or wrong signatures get 401.
                </p>

                {revealedSecret ? (
                  <div style={{
                    background: '#fef3c7',
                    border: '2px solid #fbbf24',
                    borderRadius: '4px',
                    padding: '10px',
                    marginTop: '6px',
                  }}>
                    <div style={{ fontWeight: 600, fontSize: '13px', color: '#78350f', marginBottom: '4px' }}>
                      ⚠️ Copy this secret now — it will not be shown again
                    </div>
                    <div style={{
                      fontFamily: 'monospace',
                      fontSize: '12px',
                      background: 'white',
                      padding: '8px',
                      border: '1px solid #e2e8f0',
                      borderRadius: '4px',
                      wordBreak: 'break-all',
                    }}>
                      {revealedSecret}
                    </div>
                    <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                      <button
                        type="button"
                        onClick={() => {
                          navigator.clipboard.writeText(revealedSecret);
                        }}
                        style={{
                          padding: '4px 10px',
                          background: '#0f172a',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '12px',
                        }}
                      >
                        📋 Copy
                      </button>
                      <button
                        type="button"
                        onClick={() => setRevealedSecret(null)}
                        style={{
                          padding: '4px 10px',
                          background: 'white',
                          color: '#0f172a',
                          border: '1px solid #cbd5e1',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '12px',
                        }}
                      >
                        Done (hide)
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={handleRotateSecret}
                    disabled={rotating}
                    style={{
                      padding: '6px 14px',
                      background: hasSigningSecret ? '#ef4444' : '#10b981',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: rotating ? 'wait' : 'pointer',
                      fontSize: '13px',
                    }}
                  >
                    {rotating ? 'Rotating…' : hasSigningSecret ? '🔄 Rotate Secret' : '🔑 Generate Signing Secret'}
                  </button>
                )}
              </div>
            )}

            {!form?.id && (
              <p style={{ fontSize: '12px', color: '#94a3b8', fontStyle: 'italic' }}>
                Save the form first, then you can generate a signing secret.
              </p>
            )}
          </div>
        </form>
    </ModalShell>
  );
};

export default FormDialog;
